import type { ReportedLogEntry } from '@bidmanager/shared';

/**
 * Client-side failure reporting for the client portal.
 *
 * The twin of `apps/admin/src/log.ts` — same shape, different `SOURCE`, exactly
 * as the two `api.ts` files are. Kept as two small files rather than a shared
 * package export because `@bidmanager/shared` is imported by the API too, and
 * it is deliberately free of anything that touches `window`.
 *
 * What it guarantees, in rough order of how badly each one bites when missing:
 *
 *  - **It cannot loop.** A failure while reporting is never itself reported.
 *  - **It cannot flood.** Identical failures inside a short window collapse to
 *    one report — a render loop throws hundreds of times a second.
 *  - **It cannot block.** Reports are queued and flushed on a timer, with
 *    `keepalive` so the last batch survives the page being closed.
 *  - **It cannot fail the app.** Every path swallows.
 */

const SOURCE = 'WEB' as const;
const ENDPOINT = '/api/logs';
const MAX_QUEUE = 20;
const FLUSH_DELAY_MS = 2_000;
const DEDUPE_WINDOW_MS = 10_000;

let queue: ReportedLogEntry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
/** True while a report is in flight — a failure inside this must stay silent. */
let reporting = false;
const recent = new Map<string, number>();
let accessToken: () => string | null = () => null;

/**
 * Let the API client supply the current access token.
 *
 * The endpoint is public, so this is not authentication — it is attribution.
 * Without it every browser report is anonymous, and "which client hit this bug"
 * is the first question anyone asks. The server reads identity from the token
 * and ignores the body, so this is the only way a report can carry a tenant.
 */
export function setAccessTokenSource(source: () => string | null): void {
  accessToken = source;
}

function isDuplicate(key: string): boolean {
  const now = Date.now();
  for (const [seen, at] of recent) {
    if (now - at > DEDUPE_WINDOW_MS) recent.delete(seen);
  }
  const last = recent.get(key);
  recent.set(key, now);
  return last !== undefined && now - last < DEDUPE_WINDOW_MS;
}

export function reportIssue(
  scope: string,
  error: unknown,
  options: { level?: 'ERROR' | 'WARN'; code?: string; context?: Record<string, unknown> } = {},
): void {
  if (reporting) return;

  try {
    const message = describe(error);
    if (isDuplicate(`${scope}|${message}`)) return;
    if (queue.length >= MAX_QUEUE) return;

    queue.push({
      level: options.level ?? 'ERROR',
      source: SOURCE,
      scope,
      message,
      ...(options.code ? { code: options.code } : {}),
      ...(error instanceof Error && error.stack ? { stack: error.stack } : {}),
      context: {
        // The route, not the full URL: a path is enough to place the failure
        // and query strings are where tokens hide.
        path: window.location.pathname,
        ...options.context,
      },
      occurredAt: new Date().toISOString(),
    });

    if (!timer) timer = setTimeout(() => void flush(), FLUSH_DELAY_MS);
  } catch {
    // Reporting must never be the thing that breaks the page.
  }
}

export async function flush(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0 || reporting) return;

  const entries = queue;
  queue = [];
  reporting = true;
  try {
    let token: string | null = null;
    try {
      token = accessToken();
    } catch {
      // An anonymous report still beats no report.
    }

    await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ entries }),
      // Survives an unload: the most interesting failure is often the one just
      // before the user closed the tab.
      keepalive: true,
    });
  } catch {
    // The API is unreachable — which is very likely what we were reporting.
    // Dropped rather than retried: a retry loop against a dead API is worse
    // than a lost line.
  } finally {
    reporting = false;
  }
}

/**
 * Global handlers.
 *
 * `error` catches uncaught exceptions, including the ones React re-throws from
 * a failed render; `unhandledrejection` catches the async half, which is where
 * most real failures in this app live.
 */
export function installErrorReporting(): void {
  window.addEventListener('error', (event) => {
    reportIssue('window', event.error ?? event.message, {
      context: {
        ...(event.filename ? { file: event.filename } : {}),
        ...(event.lineno ? { line: event.lineno } : {}),
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportIssue('unhandledrejection', event.reason);
  });

  // Anything still queued when the tab goes away.
  window.addEventListener('pagehide', () => void flush());
}

function describe(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return String(error);
}
